/* eslint-disable @typescript-eslint/no-floating-promises */
import { format } from "date-fns";
import { useMemo, useState } from "react";
import useUserPageParams from "../../../../../hooks/useUserPageParams";
import { Badge, Group, Text, Anchor, Progress } from "@mantine/core";
import { Select } from "@mantine/core";
import {
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Mail,
  List,
  Pencil,
} from "lucide-react";
import { useContact, useWorkspaceObjectSchema } from "../../../../../hooks/QueryHooks";
import useWorkspaceStore from "../../../../../store/workspace/workspaceStore";
import SourceBadge from "../../../../home/settings/data-management/components/SourceBadge";

const humanizeDuration = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h`;
  return `${Math.round(seconds / 86400)}d`;
};

function formatFieldValue(
  value: string | number | boolean | null | undefined,
  type: string,
): string {
  if (value === null || value === undefined || value === "") return "\u2014";

  const raw = String(value);

  switch (type) {
    case "currency": {
      if (raw.startsWith("$")) return raw;
      const num = parseFloat(raw.replace(/[^0-9.-]/g, ""));
      if (Number.isNaN(num)) return raw;
      if (Math.abs(num) >= 1_000_000) return `$${(num / 1_000_000).toFixed(1)}M`;
      if (Math.abs(num) >= 1_000) return `$${num.toLocaleString("en-US")}`;
      return `$${num.toFixed(2)}`;
    }
    case "number":
    case "int":
    case "float": {
      const num = parseFloat(raw.replace(/,/g, ""));
      if (Number.isNaN(num)) return raw;
      return num.toLocaleString("en-US");
    }
    case "percentage":
    case "percent": {
      const num = parseFloat(raw.replace(/[^0-9.-]/g, ""));
      if (Number.isNaN(num)) return raw;
      return `${num}%`;
    }
    case "datetime":
    case "date": {
      try {
        const d = new Date(raw);
        if (Number.isNaN(d.getTime())) return raw;
        const hasTime = d.getHours() !== 0 || d.getMinutes() !== 0 || d.getSeconds() !== 0;
        if (hasTime) {
          return d.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
          });
        }
        return d.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
      } catch {
        return raw;
      }
    }
    case "boolean": {
      const lower = raw.toLowerCase();
      if (lower === "true" || lower === "1" || lower === "yes") return "Yes";
      if (lower === "false" || lower === "0" || lower === "no") return "No";
      return raw;
    }
    default:
      return raw;
  }
}

function getFieldValueClassName(
  value: string | number | boolean | null | undefined,
  type: string,
): string {
  if (value === null || value === undefined || value === "") return "text-gray-400";

  switch (type) {
    case "boolean": {
      const lower = String(value).toLowerCase();
      if (lower === "true" || lower === "1" || lower === "yes") return "text-green-600";
      if (lower === "false" || lower === "0" || lower === "no") return "text-gray-400";
      return "";
    }
    case "currency":
    case "number":
    case "int":
    case "float":
      return "font-mono tabular-nums";
    case "percentage":
    case "percent":
      return "font-mono tabular-nums";
    default:
      return "";
  }
}

const UserTraitsTab = () => {
  const { userId }: any = useUserPageParams();
  const workspaceId = useWorkspaceStore((state) => state.workspace?.id);
  const { data: contact }: any = useContact({
    workspaceId,
    enabled: !!workspaceId,
    contactId: userId,
    retry: 2,
  });
  const { data: workspaceSchema }: any = useWorkspaceObjectSchema({
    workspaceId,
    enabled: !!workspaceId,
    retry: 2,
  });
  const userTraits = workspaceSchema?.fieldsByObjectSlug?.users || { list: [], obj: {} };

  const [collapsedSources, setCollapsedSources] = useState<Set<string>>(new Set());
  const [selectedSource, setSelectedSource] = useState<string | null>("all");

  const toggleSource = (source: string) => {
    setCollapsedSources((prev) => {
      const next = new Set(prev);
      if (next.has(source)) {
        next.delete(source);
      } else {
        next.add(source);
      }
      return next;
    });
  };

  const groupedTraits: Record<string, any[]> = useMemo(
    () =>
      Object.entries(contact || {}).reduce((groups: Record<string, any[]>, [trKey, trValue]: any) => {
        const selectedTrait = userTraits?.obj?.[`users.${trKey}`];

        if (!selectedTrait?.exclude_display && selectedTrait) {
          const source = selectedTrait.source || "native";

          if (!groups[source]) {
            groups[source] = [];
          }

          groups[source].push({
            key: trKey,
            ...selectedTrait,
            val: trValue,
          });
        }

        return groups;
      }, {}),
    [userTraits?.obj, contact]
  );

  const sourceOptions = useMemo(() => {
    const sources = Object.keys(groupedTraits);
    return [
      { value: "all", label: "All Sources" },
      ...sources.map((s) => ({
        value: s,
        label: s.charAt(0).toUpperCase() + s.slice(1),
      })),
    ];
  }, [groupedTraits]);

  const sourceEntries = useMemo(() => {
    const all = Object.entries(groupedTraits);
    if (!selectedSource || selectedSource === "all") return all;
    return all.filter(([source]) => source === selectedSource);
  }, [groupedTraits, selectedSource]);

  const totalTraits = sourceEntries.reduce((s, [, traits]) => s + traits.length, 0);

  const renderTraitValue = (trait: any) => {
    const { val, data_type: dataType, display_format: displayFormat, display_rules, field_type } = trait;

    if (val === null || val === undefined || (val === "" && dataType !== "boolean")) {
      return <span className="text-xs text-gray-400">{"\u2014"}</span>;
    }

    if (field_type === "composite" && display_rules?.type === "health_score") {
      const score = typeof val === "number" ? val : parseFloat(val);
      const color = score >= 70 ? "green" : score >= 40 ? "yellow" : "red";
      return (
        <Group gap={8}>
          <Progress value={score} color={color} size="sm" className="w-20" />
          <Text size="xs" fw={500} c={color}>{score}</Text>
        </Group>
      );
    }

    if (displayFormat) {
      switch (displayFormat) {
        case "percentage":
          return (
            <span className="text-xs font-mono tabular-nums">
              {typeof val === "number" ? `${val}%` : val}
            </span>
          );
        case "currency": {
          const formatted = formatFieldValue(val, "currency");
          return <span className="text-xs font-mono tabular-nums">{formatted}</span>;
        }
        case "decimal_1":
          return <span className="text-xs">{typeof val === "number" ? val.toFixed(1) : val}</span>;
        case "decimal_2":
          return <span className="text-xs">{typeof val === "number" ? val.toFixed(2) : val}</span>;
        case "duration_seconds":
          return <span className="text-xs">{typeof val === "number" ? humanizeDuration(val) : val}</span>;
        default:
          break;
      }
    }

    if (dataType === "list" && Array.isArray(val)) {
      return (
        <div className="flex items-center gap-1 flex-wrap justify-end">
          {val.map((item: any, idx: number) => (
            <Badge key={idx} variant="light" color="gray" size="xs">
              {String(item)}
            </Badge>
          ))}
        </div>
      );
    }

    if (dataType === "email" || (typeof val === "string" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val))) {
      return (
        <Anchor href={`mailto:${val}`} size="xs" className="flex items-center gap-1">
          <Mail className="h-3 w-3" />
          {val}
        </Anchor>
      );
    }

    if (typeof val === "string" && (val.startsWith("http://") || val.startsWith("https://"))) {
      return (
        <Anchor href={val} target="_blank" size="xs" className="flex items-center gap-1">
          <span className="truncate max-w-[200px]">{val}</span>
          <ExternalLink className="h-3 w-3" />
        </Anchor>
      );
    }

    const resolvedType = displayFormat || dataType || "string";
    const formatted = formatFieldValue(val, resolvedType);
    const className = getFieldValueClassName(val, resolvedType);

    return <span className={`text-xs text-gray-900 ${className}`}>{formatted}</span>;
  };

  if (sourceEntries.length === 0 && (!selectedSource || selectedSource === "all")) {
    return (
      <div className="max-w-2xl">
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center mb-4">
            <List className="h-6 w-6 text-gray-400" />
          </div>
          <h4 className="text-sm font-medium text-gray-900 mb-1">No traits available</h4>
          <p className="text-xs text-gray-500 max-w-[280px]">
            Traits for this contact will appear here once data is synced.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-900">
          Traits <span className="text-gray-500 font-normal ml-1">{totalTraits}</span>
        </h3>
        <div className="flex items-center gap-2">
          <Select
            value={selectedSource}
            onChange={setSelectedSource}
            data={sourceOptions}
            size="xs"
            classNames={{
              input: "h-7 text-xs rounded-md",
              root: "w-[140px]",
            }}
          />
          <button
            type="button"
            className="flex items-center gap-1.5 h-7 px-2 text-xs text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
          >
            <Pencil className="h-3 w-3" />
            Edit
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {sourceEntries.map(([source, sourceTraits]) => {
          const isCollapsed = collapsedSources.has(source);

          return (
            <div key={source} className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => toggleSource(source)}
                className="w-full flex items-center gap-2 px-4 py-2.5 bg-gray-50 hover:bg-gray-100/80 transition-colors"
              >
                {isCollapsed ? (
                  <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
                )}
                <SourceBadge source={source} showIcon size="sm" />
                <span className="text-xs font-medium text-gray-900">
                  {source.charAt(0).toUpperCase() + source.slice(1)}
                </span>
                <span className="text-[11px] text-gray-500 ml-auto">
                  {sourceTraits.length} trait{sourceTraits.length !== 1 ? "s" : ""}
                </span>
              </button>

              {!isCollapsed && (
                <div>
                  {sourceTraits.map((trait, idx) => (
                    <div
                      key={trait.key}
                      className={`flex items-center justify-between px-4 py-2.5${
                        idx < sourceTraits.length - 1 ? " border-b border-gray-200" : ""
                      }`}
                    >
                      <span className="text-xs text-gray-500">
                        {trait.display_name || trait.key.replace(/_/g, " ")}
                      </span>
                      <div className="text-xs text-gray-900">{renderTraitValue(trait)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UserTraitsTab;
